import uniq from "lodash/uniq";
import sortBy from "lodash/sortBy";
import {
  CreditRate,
  RateUnit,
  ActivityParticipationRole,
  type ActivityTypeKind,
  AttendanceType,
  SectionAvailability
} from "./types";

export type { ActivityTypeKind } from "./types";

export const RATE_UNIT_MAP: Record<RateUnit, string> = {
  [RateUnit.HOUR]: "hr",
  [RateUnit.DAY]: "day",
  [RateUnit.WEEK]: "week",
  [RateUnit.EVENT_SESSION]: "session",
  [RateUnit.EVENT]: "video",
  [RateUnit.SEASON]: "season",
  [RateUnit.INSTANCE]: "instance"
};

const PARTICIPATION_ROLE_MAP: Record<string, string> = {
  [ActivityParticipationRole.HOST_PROFESSIONAL]: "Host Professional",
  [ActivityParticipationRole.EXAMINER]: "Examiner",
  [ActivityParticipationRole.ATTENDEE]: "Attendee",
  [ActivityParticipationRole.ONE_TO_FOUR_VOLUNTEER_HOURS]: "1-4 Volunteer Hours",
  [ActivityParticipationRole.FIVE_TO_EIGHT_VOLUNTEER_HOURS]: "5-8 Volunteer Hours"
};
const ATTENDANCE_TYPE_MAP = {
  [AttendanceType.IN_PERSON]: "In-Person",
  [AttendanceType.VIRTUAL]: "Virtual"
};

const SECTION_AVAILABILITY_MAP = {
  [SectionAvailability.CAN_CREATE]: "Can Create",
  [SectionAvailability.CAN_VIEW]: "Can View"
};
const formatValueRange = (sortedUniqueValues: number[]) => {
  if (sortedUniqueValues.length === 0) {
    return "";
  }

  if (sortedUniqueValues.length === 1) {
    return sortedUniqueValues[0];
  }

  return `${sortedUniqueValues[0]}-${sortedUniqueValues[sortedUniqueValues.length - 1]}`;
};

export const renderCreditRate = (creditRates: CreditRate[], duration: number = 1) => {
  if (creditRates.length === 0) {
    return "";
  }

  const uniqueMultipliers = sortBy(uniq(creditRates.map((creditRate) => creditRate.rateMultiplier * duration)));
  const rateMultiplierRange = formatValueRange(uniqueMultipliers);

  return `${rateMultiplierRange}/${renderRateUnit(creditRates[0]?.rateUnit)}`;
};

export const renderCredits = (creditRates: CreditRate[], duration: number) => {
  if (creditRates.length === 0) {
    return "";
  }

  const uniqueCredits = sortBy(uniq(creditRates.map((creditRate) => Math.round(creditRate.rateMultiplier * duration))));
  const creditsRange = formatValueRange(uniqueCredits);

  return `${creditsRange} ${creditRates[0]?.legacyCreditType}`;
};

const renderRateUnit = (rateUnit: RateUnit) => {
  return RATE_UNIT_MAP[rateUnit];
};

export const renderParticipationRole = (participationRole: ActivityParticipationRole | string) => {
  return PARTICIPATION_ROLE_MAP[participationRole] ?? participationRole;
};

export const renderAttendanceType = (attendanceType: AttendanceType) => {
  return ATTENDANCE_TYPE_MAP[attendanceType];
};

export const renderSectionAvailability = (sectionAvailability: SectionAvailability) => {
  return SECTION_AVAILABILITY_MAP[sectionAvailability];
};

/** Single activity type check by title and kind. See ActivityTypeKind in ./types. */
export const isActivityType = (title: string | undefined, kind: ActivityTypeKind): boolean => {
  if (!title) return false;
  switch (kind) {
    case "education-self-paced":
      return title.includes("Self-Paced");
    case "instructor-led":
      return title.includes("Instructor-Led");
    case "education-self-reported":
      return title.includes("Self-Reported");
    case "pat":
      return title === "PAT";
    case "national-meeting":
      return title === "National Meeting";
    case "recruitment":
      return title === "Recruitment";
    case "championship":
      return title.includes("Championship Registration");
    case "education":
      return title.includes("University Seminar");
    default:
      return false;
  }
};

export const getParticipationRoleOptions = (creditRates: CreditRate[]) => {
  return creditRates.map((creditRate) => ({
    value: creditRate.participationRole,
    label: renderParticipationRole(creditRate.participationRole)
  }));
};

export const getAttendanceTypeOptions = (creditRates: CreditRate[]) => {
  return creditRates.map((creditRate) => ({
    value: creditRate.attendanceType,
    label: renderAttendanceType(creditRate.attendanceType)
  }));
};
